import { useState, useEffect, useCallback } from 'react';
import { Category, Tutorial } from '@/types';
import { INITIAL_CATEGORIES, INITIAL_TUTORIALS } from '@/data/initialData';

const STORAGE_KEY_TUTORIALS = 'vx_tutorials';
const STORAGE_KEY_CATEGORIES = 'vx_categories';

export function useAppData() {
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // Load data from localStorage or initialData
  useEffect(() => {
    const savedTutorials = localStorage.getItem(STORAGE_KEY_TUTORIALS);
    const savedCategories = localStorage.getItem(STORAGE_KEY_CATEGORIES);

    if (savedTutorials) {
      setTutorials(JSON.parse(savedTutorials));
    } else {
      setTutorials(INITIAL_TUTORIALS);
      localStorage.setItem(STORAGE_KEY_TUTORIALS, JSON.stringify(INITIAL_TUTORIALS));
    }

    if (savedCategories) {
      setCategories(JSON.parse(savedCategories));
    } else {
      setCategories(INITIAL_CATEGORIES);
      localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(INITIAL_CATEGORIES));
    }
  }, []);

  // Admin actions
  const addTutorial = useCallback((tutorial: Tutorial) => {
    setTutorials((prev) => {
      const updated = [...prev, tutorial];
      localStorage.setItem(STORAGE_KEY_TUTORIALS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updateTutorial = useCallback((id: string, tutorial: Partial<Tutorial>) => {
    setTutorials((prev) => {
      const updated = prev.map((t) => (t.id === id ? { ...t, ...tutorial } : t));
      localStorage.setItem(STORAGE_KEY_TUTORIALS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const deleteTutorial = useCallback((id: string) => {
    setTutorials((prev) => {
      const updated = prev.filter((t) => t.id !== id);
      localStorage.setItem(STORAGE_KEY_TUTORIALS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updateCategories = useCallback((newCategories: Category[]) => {
    setCategories(newCategories);
    localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(newCategories));
  }, []);

  return {
    tutorials,
    categories,
    addTutorial,
    updateTutorial,
    deleteTutorial,
    updateCategories,
  };
}
